import { RefObject, useLayoutEffect } from "react";

import { clampPopoverLeft } from "./clampPopoverLeft";

/**
 * The width a panel wants, not the width it was squeezed into.
 *
 * A `position: fixed` panel with no explicit width shrinks to fit the room left of the viewport
 * edge — so its rendered width near the edge is exactly the number the clamp must not trust
 * (PREC-4130). Measure it at `max-content` for a moment instead; a panel that sets its own width
 * (often the trigger's) is measured as rendered.
 */
const naturalWidth = (panel: HTMLElement, rendered: number): number => {
	if (panel.style.width) {
		return rendered;
	}

	panel.style.width = "max-content";
	const measured = panel.getBoundingClientRect().width;

	panel.style.width = "";

	return Math.max(measured, rendered);
};

/**
 * Keeps an open portalled panel horizontally inside the viewport, whatever its own positioning
 * math decided (PREC-4138).
 *
 * Runs after every render while open: it measures where the panel actually landed and, when it
 * runs past an edge, moves its computed `left` by exactly the overflow `clampPopoverLeft` reports.
 * Working from the rendered rect rather than each caller's arithmetic is the point — the twenty
 * panels this drained each computed `left` differently (trigger left, trigger right minus a guess,
 * scroll offsets), and only the result is common to all of them. When React next writes a new
 * `left` (the trigger moved), the effect runs again and re-clamps; while nothing changes it is a
 * measurement and a no-op.
 */
export function useViewportClampedPanel(
	panelRef: RefObject<HTMLElement | null>,
	isOpen: boolean
): void {
	useLayoutEffect(() => {
		const panel = panelRef.current;

		if (!isOpen || !panel) {
			return;
		}

		const rect = panel.getBoundingClientRect();

		// A box with no size is not laid out (hidden, display: none, or jsdom) — nothing to clamp.
		if (rect.width === 0 && rect.height === 0) {
			return;
		}

		const clamped = clampPopoverLeft(rect.left, naturalWidth(panel, rect.width), window.innerWidth);
		const shift = clamped - rect.left;

		if (Math.abs(shift) < 1) {
			return;
		}

		const currentLeft = parseFloat(getComputedStyle(panel).left);

		if (Number.isFinite(currentLeft)) {
			panel.style.left = `${currentLeft + shift}px`;
		}
	});
}
